import React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants } from 'utils';
import { Modal } from 'components';
import { CopyIcon, WalletFilledIcon, ProfileFillIcon, ReferralWithdrawalIcon } from 'assets/icons';
import { ReferralService, ProfileService } from 'services';
import moment from 'moment';
import ModalWithdrawal from './modal-withdrawal';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

// Pantalla "Mis Referidos" -- equivalente web de Mobile
// (src/screens/referral/referral.tsx). Mismo layout: código + copiar, 3
// tarjetas (Tokens/Referidos/Solicitar Retiro), historial de actividad.
class ClientReferrals extends React.Component<Props> {
  state = {
    referral_code: null as string | null,
    balance: 0,
    referred_users_count: 0,
    movements: [] as any[],
    hasPaymentMobile: false,
    visibleWithdrawal: false,
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: {
        title: 'Mis Referidos',
        onBack: null,
      },
    });

    this.props.dispatch({
      type: 'SET_SUBHEADER',
      payload: {
        title: '',
      },
    });

    this.load();
  }

  load = async (withoutLoading: boolean = false) => {
    try {
      const me: any = await ReferralService.client.getMe({ withoutLoading });
      const movementsRes: any = await ReferralService.client.getMovements({
        withoutLoading,
        per_page: 20,
      });
      // Mismos 3 campos que exige el backend en requestWithdrawal() -- si
      // falta alguno, el modal de retiro bloquea el flujo (ver profile.tsx).
      const profileRes: any = await ProfileService.get({
        user_id: this.props.user.id,
        withoutLoading,
      });
      const bankData = profileRes.bank_data;

      this.setState({
        referral_code: me.referral_code,
        balance: me.balance || 0,
        referred_users_count: me.referred_users_count || 0,
        movements: movementsRes.rows || [],
        hasPaymentMobile: !!(
          bankData?.payment_mobile_bank_id &&
          bankData?.payment_mobile_phone &&
          bankData?.payment_mobile_document
        ),
      });
    } catch (e) {
      console.log('ClientReferrals load error:', e);
    }
  };

  copyCode = () => {
    if (!this.state.referral_code) return;
    navigator.clipboard.writeText(this.state.referral_code);
    Globals.showSuccess('Código copiado');
  };

  // Formato compacto -- mismo patrón que Mobile (referral.tsx): a partir de
  // 1000 se muestra como "N.NK", TRUNCADO hacia abajo (no redondeado).
  formatCompactBalance = (value: number): string => {
    if (value < 1000) return value.toFixed(0);
    const compact = value / 1000;
    const truncated = Math.floor(compact * 10) / 10;
    return `${truncated % 1 === 0 ? truncated.toFixed(0) : truncated.toFixed(1)}K`;
  };

  getMovementLabel = (movement: any): { title: string; subtitle: string } => {
    switch (movement.type) {
      case Constants.REFERRAL_MOVEMENT_TYPES.EARNED_FIRST_PURCHASE:
      case Constants.REFERRAL_MOVEMENT_TYPES.EARNED_RENEWAL: {
        const buyer = movement.plan_payment?.user;
        const buyerName =
          buyer?.full_name || [buyer?.name, buyer?.lastname].filter(Boolean).join(' ') || 'Usuario';
        const membershipName = movement.plan_payment?.membership?.name;
        return {
          title: buyerName,
          subtitle: membershipName ? `Compra ${membershipName}` : 'Compra de membresía',
        };
      }
      case Constants.REFERRAL_MOVEMENT_TYPES.WITHDRAWAL: {
        const status = movement.withdrawal_request?.status;
        const isRejected = status === Constants.REFERRAL_WITHDRAWAL_STATUS.REJECTED;
        const isRefund = isRejected && Number(movement.amount) >= 0;
        const statusText =
          status === Constants.REFERRAL_WITHDRAWAL_STATUS.PAID
            ? 'Solicitud de retiro pagada'
            : isRefund
              ? 'Solicitud de retiro rechazada'
              : 'Solicitud de retiro';
        return { title: isRefund ? 'Reembolso' : 'Retiro', subtitle: statusText };
      }
      default:
        return { title: 'Movimiento', subtitle: '' };
    }
  };

  render() {
    const {
      referral_code,
      balance,
      referred_users_count,
      movements,
      hasPaymentMobile,
      visibleWithdrawal,
    } = this.state;

    return (
      <div id='client-referrals'>
        <Modal
          className='modal-withdrawal-referral'
          visible={visibleWithdrawal}
          title='Retiro'
          onClose={() => this.setState({ visibleWithdrawal: false })}
        >
          <ModalWithdrawal
            balance={balance}
            hasPaymentMobile={hasPaymentMobile}
            onGoToProfile={() => {
              this.setState({ visibleWithdrawal: false });
              this.props.history.push('/profile');
            }}
            onSuccess={() => {
              this.setState({ visibleWithdrawal: false });
              this.load(true);
            }}
            onClose={() => this.setState({ visibleWithdrawal: false })}
          />
        </Modal>

        <div className='container-code'>
          <p className='code-label'>Código de referido</p>
          <div className='code-row'>
            <span className='code'>{referral_code || '-'}</span>
            <button className='btn-copy' onClick={this.copyCode}>
              <img src={CopyIcon} />
              <span>Copiar</span>
            </button>
          </div>
        </div>

        <div className='row row-cards'>
          <div className='col-md-4'>
            <div className='card-referral card-light'>
              <img src={WalletFilledIcon} className='card-icon' />
              <p className='card-value'>{this.formatCompactBalance(balance)}</p>
              <p className='card-label'>Tokens</p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='card-referral card-light'>
              <img src={ProfileFillIcon} className='card-icon' />
              <p className='card-value'>{referred_users_count}</p>
              <p className='card-label'>Referidos</p>
            </div>
          </div>
          <div className='col-md-4'>
            <button
              className='card-referral card-dark'
              onClick={() => this.setState({ visibleWithdrawal: true })}
            >
              <img src={ReferralWithdrawalIcon} className='card-icon card-icon-dark' />
              <p className='card-dark-text'>Solicitar Retiro</p>
            </button>
          </div>
        </div>

        <p className='section-title'>Historial de actividad</p>

        {movements.length === 0 && <p className='empty-text'>Aún no tienes movimientos</p>}

        <div className='container-movements'>
          {movements.map((movement: any, index: number) => {
            const { title, subtitle } = this.getMovementLabel(movement);
            const isPositive = Number(movement.amount) >= 0;

            return (
              <div className='movement-row' key={movement.id ?? index}>
                <div className='movement-info'>
                  <p className='movement-title'>{title}</p>
                  <p className='movement-subtitle'>{subtitle}</p>
                </div>
                <div className='movement-amount-container'>
                  <p className={`movement-amount ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '+' : ''}
                    {Number(movement.amount).toFixed(0)}
                  </p>
                  <p className='movement-date'>
                    {moment(movement.created_at).format('DD-MM-YYYY')}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(ClientReferrals);
